/** As the templates API sends it (module `templates`; the web app does not import server modules). */
export type TemplateLine = { itemId: string | null; kitId: string | null; quantity: number; name: string; categoryId: string | null; categoryName: string | null };
/** `lines` is absent for a role that may not see the content of blocks. */
export type Block = { id: string; name: string; notes: string; lines?: TemplateLine[]; version: number };
export type BlockSummary = { id: string; name: string; notes?: string; lineCount: number; version: number };
export type BlockUse = { blockId: string; name: string; multiplier: number; lines?: TemplateLine[] };
export type Template = {
  id: string;
  name: string;
  notes?: string;
  scopes?: string[];
  systemId?: string | null;
  blocks?: BlockUse[];
  lines?: TemplateLine[];
  checklist?: string[];
  version: number;
};
export type TemplateSummary = { id: string; name: string; notes?: string; scopes?: string[]; systemId?: string | null; blockCount: number; lineCount: number; version: number };

export const BLOCKS_RESOURCE = 'templates.blocks';
export const TEMPLATES_RESOURCE = 'templates.templates';
/** Kits (another module) are a tab of "Szablony" (owner 20.10 23:37). */
export const KITS_RESOURCE = 'kits.kits';
export const TEMPLATES_KEY = ['templates'] as const;

/** A line being edited: the quantity stays text until it is saved. */
export type DraftLine = { key: number; itemId: string | null; kitId: string | null; name: string; categoryId: string | null; categoryName: string | null; quantity: string };

let nextKey = 1;
export const newKey = () => nextKey++;
export const toDraft = (l: TemplateLine): DraftLine => ({ key: newKey(), itemId: l.itemId, kitId: l.kitId, name: l.name, categoryId: l.categoryId, categoryName: l.categoryName, quantity: String(l.quantity) });

/** Lines as the API takes them. */
export const toWire = (lines: readonly DraftLine[]) => lines.map((l) => (l.itemId ? { itemId: l.itemId, quantity: Number(l.quantity) } : { kitId: l.kitId, quantity: Number(l.quantity) }));
export const validQuantities = (lines: readonly DraftLine[]) => lines.every((l) => /^\d+$/.test(l.quantity.trim()) && Number(l.quantity) > 0 && Number(l.quantity) <= 1_000_000);

const NO_CATEGORY = 'Bez kategorii';
const KITS = 'Zestawy';

/**
 * Lines grouped as on the packlist: categories in the catalog's order (`order`: category labels in that order;
 * a category not in it keeps the place the server gave it), "Bez kategorii" after them, kits last.
 */
export function groupLines<T extends Pick<DraftLine, 'kitId' | 'categoryId' | 'categoryName'>>(lines: readonly T[], order: readonly { id: string; label: string }[]): Array<[string, T[]]> {
  const labelOf = new Map(order.map((c) => [c.id, c.label]));
  const rank = new Map(order.map((c, i) => [c.label, i]));
  const groups = new Map<string, T[]>();
  for (const l of lines) {
    const label = l.kitId ? KITS : (l.categoryId && (labelOf.get(l.categoryId) ?? l.categoryName)) || NO_CATEGORY;
    groups.set(label, [...(groups.get(label) ?? []), l]);
  }
  const place = (label: string) => (label === KITS ? 3 : label === NO_CATEGORY ? 2 : rank.has(label) ? 0 : 1);
  const seen = [...groups.keys()];
  return [...groups.entries()].sort(([a], [b]) => place(a) - place(b) || (rank.get(a) ?? seen.indexOf(a)) - (rank.get(b) ?? seen.indexOf(b)));
}
